"use client";

import { Col, Row, Divider, Card, Image, Tag } from "antd";
import Title from "../common/Typography/Title";
import { useAppSelector } from "@/lib/redux/hooks";
import Paragraph from "../common/Typography/Paragraph";
import Text from "../common/Typography/Text";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCalendarAlt, faTag } from "@fortawesome/free-solid-svg-icons";
import Link from "@/components/common/LinkNext";
import { useBlogs } from "@/hooks/blog/useBlogs";
import { useTETranslation } from "@/hooks/app/useTETranslation";
import { sanitizeCmsHtml } from "@/utils/html/sanitizeCmsHtml";
import { useTranslation } from "react-i18next";
import { useBlogDetail } from "./BlogDetailContainer";

const BlogDetailContent = () => {
  const blogFromStore = useAppSelector((state) => state.blog.blog_detail.data);
  const blogFromContext = useBlogDetail();
  const blogDetail = blogFromContext?.title ? blogFromContext : blogFromStore;
  const { data: allBlogs } = useBlogs(blogDetail?.category || "TRAVEL");
  const { getValidDayjs } = useTETranslation()
  const { t } = useTranslation()

  // Get related articles by the same category (excluding current article)
  const relatedArticles =
    allBlogs
      ?.filter(
        (blog) =>
          blog.id !== blogDetail?.id &&
          blog.category === blogDetail?.category
      )
      .slice(0, 3) || [];

  if (!blogDetail?.title) {
    return null
  }

  return (
    <Col span={24} style={{ padding: "0 1rem" }}>
      <Row>
        <Title
          level={1}
          style={{
            marginBottom: "0.5rem",
            fontSize: "1.75rem",
            lineHeight: "1.3",
          }}
        >
          {blogDetail.title}
        </Title>
      </Row>
      {blogDetail.created_at && (
        <Row style={{ marginBottom: "0.5rem" }}>
          <div className="published-date">
            <FontAwesomeIcon icon={faCalendarAlt} />
            <Text type="secondary">
              {t("blog.post_date")}:{" "}
              {getValidDayjs(blogDetail.created_at).format(
                "DD/MM/YYYY HH:mm"
              )}
            </Text>
          </div>
        </Row>
      )}

      <Divider style={{ margin: "1.5rem 0" }} />
      <Row>
        <Col
          span={24}
          className="blog-content rich-text"
          dangerouslySetInnerHTML={{ __html: sanitizeCmsHtml(blogDetail.content_html) }}
        />
      </Row>
      {blogDetail.tags && blogDetail.tags.length > 0 && (
        <Row style={{ marginBottom: "1.5rem" }}>
          <div
            className="blog-tags"
            style={{
              display: "flex",
              alignItems: "center",
              gap: "8px",
              flexWrap: "wrap",
            }}
          >
            <FontAwesomeIcon
              icon={faTag}
              style={{ color: "#666", fontSize: "14px" }}
            />
            <Text type="secondary" style={{ marginRight: "8px" }}>
              Tags:
            </Text>
            {blogDetail.tags.map((tag, index) => (
              <Tag
                key={index}
                color="blue"
                style={{
                  fontSize: "12px",
                  padding: "2px 8px",
                  borderRadius: "12px",
                  border: "none",
                  background: "#e6f7ff",
                  color: "#1890ff",
                }}
              >
                {tag}
              </Tag>
            ))}
          </div>
        </Row>
      )}

      {/* Related Articles Section */}
      {relatedArticles.length > 0 && (
        <>
          <Divider style={{ marginTop: "3rem", marginBottom: "2rem" }} />
          <Row>
            <Title level={4} style={{ marginBottom: "1.5rem" }}>
              {t("blog.featured_article")}
            </Title>
          </Row>
          <Row gutter={[16, 16]} className="related-articles">
            {relatedArticles.map((article) => (
              <Col key={article.id} xs={24} sm={12} md={8}>
                <Link href={`/explore/${article.slug}`}>
                  <Card
                    hoverable
                    cover={
                      <div className="related-article-image">
                        <Image
                          alt={article.title}
                          src={article.thumbnail_url}
                          width="100%"
                          height={200}
                          style={{ objectFit: "cover" }}
                          preview={false}
                        />
                      </div>
                    }
                    style={{ height: "100%" }}
                    styles={{
                      body: {
                        padding: "16px"
                      }
                    }}
                  >
                    <Text
                      type="secondary"
                      style={{ fontSize: "12px", marginBottom: "1rem" }}
                    >
                      <FontAwesomeIcon icon={faCalendarAlt} />{" "}
                      {getValidDayjs(article.created_at).format("DD/MM/YYYY")}
                    </Text>
                    <Card.Meta
                      style={{ marginTop: "0.5rem" }}
                      title={
                        <Title
                          level={5}
                          style={{
                            margin: 0,
                            fontSize: "14px",
                            lineHeight: "1.4",
                          }}
                          ellipsis={{ rows: 2 }}
                        >
                          {article.title}
                        </Title>
                      }
                      description={
                        <div>
                          <Paragraph
                            ellipsis={{ rows: 2 }}
                            style={{
                              marginTop: "8px",
                              marginBottom: 0,
                              fontSize: "12px",
                            }}
                          >
                            {article.summary}
                          </Paragraph>
                        </div>
                      }
                    />
                  </Card>
                </Link>
              </Col>
            ))}
          </Row>
        </>
      )}
    </Col>
  );
};

export default BlogDetailContent;
